/**
 * Section B7.3 Page - B.7.3.洩漏排放處理
 * Leakage emissions handling
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import 'katex/dist/katex.min.css';
import { BlockMath, InlineMath } from 'react-katex';
import { validateSession } from '../utils/sessionValidator';
import { usePermission } from '../hooks/usePermission';
import { useProject } from '../contexts/ProjectContext';
import RichTextEditor from '../components/RichTextEditor';
import sectionB73Service, {
  SectionB73Detail, LeakageEntry, LeakageParameter
} from '../services/sectionB73Service';
import { getSystemCodes, SystemCode } from '../services/systemCodeService';
import '../styles/DataTable.css';
import LatexHelper from '../components/LatexHelper';
import FunctionPageHeader from '../components/FunctionPageHeader';
import { useToast } from '../contexts/ToastContext';
import { parseLatexSymbols, normalizeSubscripts } from '../utils/latexEvaluator';
import formulaConfigService, { FormulaConfigItem } from '../services/formulaConfigService';

// ── 常數 ──────────────────────────────────────────────────────────
type WizardStage = 'equations' | 'methodological_choices' | 'conservative_estimates';

const EMPTY_ENTRY = (): LeakageEntry => ({
  id: crypto.randomUUID(),
  methodology: '',
  equation: { latex: '' },
  equation_note: '',
  parameters: [],
  calculation_result: { value: null, unit: '' },
});

// ── 數值格式化（千分號）────────────────────────────────────────────
function fmtNum(value: number | null | undefined, decimals = 5): string {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: decimals });
}

// ── 單位 LaTeX 輸入元件 ──────────────────────────────────────────
const COMMON_UNITS = [
  { label: 'tCO₂e',  latex: '\\text{tCO}_2\\text{e}' },
  { label: 'ktCO₂e', latex: '\\text{ktCO}_2\\text{e}' },
  { label: 'MtCO₂e', latex: '\\text{MtCO}_2\\text{e}' },
  { label: 'MWh',    latex: '\\text{MWh}' },
  { label: 'GWh',    latex: '\\text{GWh}' },
  { label: 'kWh',    latex: '\\text{kWh}' },
  { label: 'GJ',     latex: '\\text{GJ}' },
  { label: 'MJ',     latex: '\\text{MJ}' },
  { label: 'kg',     latex: '\\text{kg}' },
  { label: 't',      latex: '\\text{t}' },
  { label: 'm³',     latex: '\\text{m}^3' },
  { label: 'Nm³',    latex: '\\text{Nm}^3' },
  { label: 'L',      latex: '\\text{L}' },
  { label: '%',      latex: '\\%' },
  { label: 'tCO₂e/MWh', latex: '\\text{tCO}_2\\text{e/MWh}' },
  { label: 'tCO₂e/GJ',  latex: '\\text{tCO}_2\\text{e/GJ}' },
];

interface UnitInputProps {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  compact?: boolean;
}

const UnitInput: React.FC<UnitInputProps> = ({ value, onChange, disabled = false, compact = false }) => {
  const [showPicker, setShowPicker] = useState(false);

  const preview = () => {
    if (!value.trim()) return null;
    try { return <InlineMath math={value} />; } catch { return <span style={{ color: '#c33', fontSize: 11 }}>!</span>; }
  };

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <input
          value={value}
          onChange={e => onChange(e.target.value)}
          disabled={disabled}
          placeholder={compact ? 'unit LaTeX' : '單位 LaTeX，如 \\text{tCO}_2\\text{e}'}
          style={{
            flex: 1, padding: compact ? '4px 6px' : '8px 10px',
            border: '1px solid #d0d0d0', borderRadius: 6,
            fontFamily: 'monospace', fontSize: compact ? 12 : 13,
          }}
        />
        {value.trim() && (
          <div style={{
            minWidth: compact ? 40 : 60, padding: '2px 6px',
            background: '#f8faff', border: '1px solid #d0e4ff', borderRadius: 5,
            fontSize: compact ? 12 : 13, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {preview()}
          </div>
        )}
        {!disabled && (
          <button type="button" onClick={() => setShowPicker(p => !p)}
            title="常用單位"
            style={{
              padding: '4px 7px', fontSize: 13, borderRadius: 5,
              border: '1px solid #d0d0d0', background: showPicker ? '#4A90E2' : '#fafafa',
              color: showPicker ? '#fff' : '#555', cursor: 'pointer', lineHeight: 1,
            }}>≡</button>
        )}
      </div>
      {showPicker && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, zIndex: 200, marginTop: 4,
          background: '#fff', border: '1px solid #d0d0d0', borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)', padding: '8px 10px', width: 320,
        }}>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 6, fontWeight: 600 }}>常用單位（點擊插入）</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {COMMON_UNITS.map((u, i) => (
              <button key={i} type="button"
                onClick={() => { onChange(u.latex); setShowPicker(false); }}
                style={{
                  padding: '4px 10px', fontSize: 12, borderRadius: 5,
                  border: '1px solid #e0e0e0', background: '#fafafa',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                }}>
                <span style={{ color: '#888', fontSize: 11 }}>{u.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── LaTeX 公式計算 ────────────────────────────────────────────────
function evaluateLatex(latex: string, params: LeakageParameter[]): number | null {
  try {
    let expr = latex.includes('=')
      ? latex.split(/(?<![<>!])=(?!=)/).slice(1).join('=')
      : latex;

    // 正規化頂層單字元下標（OX_y → OX_{y}）
    { let r = '', d = 0, i = 0; while (i < expr.length) { const c = expr[i]; if (c === '\\') { r += c + (expr[i+1]??''); i+=2; } else if (c === '{') { d++; r+=c; i++; } else if (c === '}') { d--; r+=c; i++; } else if (d===0 && c==='_' && i+1<expr.length && /[A-Za-z0-9]/.test(expr[i+1])) { r+='_{'+expr[i+1]+'}'; i+=2; } else { r+=c; i++; } } expr=r; }

    const filled = [...params]
      .filter(p => p.annual_value !== null && p.symbol.trim())
      .sort((a, b) => b.symbol.length - a.symbol.length);

    for (const p of filled) {
      const sym = normalizeSubscripts(p.symbol);
      const escaped = sym.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      expr = expr.replace(new RegExp(escaped, 'g'), `(${p.annual_value})`);
    }

    expr = expr
      .replace(/\\frac\{([^}]*)\}\{([^}]*)\}/g, '($1)/($2)')
      .replace(/\\sqrt\{([^}]*)\}/g, 'Math.sqrt($1)')
      .replace(/\\(?:sum|prod|int)(?:_\{[^}]*\})?(?:\^\{[^}]*\})?/g, '')
      .replace(/\\times/g, '*')
      .replace(/\\div/g, '/')
      .replace(/\\cdot/g, '*')
      .replace(/\\left\s*\(/g, '(').replace(/\\right\s*\)/g, ')')
      .replace(/\\left\s*\[/g, '(').replace(/\\right\s*\]/g, ')')
      .replace(/\[/g, '(').replace(/\]/g, ')')
      .replace(/\\left\s*\\{/g, '(').replace(/\\right\s*\\}/g, ')')
      .replace(/\^\{([^}]*)\}/g, '**($1)')
      .replace(/\^([0-9]+)/g, '**$1')
      .replace(/\\[a-zA-Z]+/g, '')
      .replace(/\{/g, '(').replace(/\}/g, ')')
      .replace(/_/g, '');

    if (/[^0-9+\-*/().%\s,MathsqrtpowPIE]/.test(expr.replace(/Math\.[a-z]+/g, ''))) return null;

    // eslint-disable-next-line no-new-func
    const result = new Function(`return (${expr})`)();
    if (typeof result !== 'number' || !isFinite(result)) return null;
    return Math.round(result * 100000) / 100000;
  } catch {
    return null;
  }
}

// ── 公式精靈元件 ──────────────────────────────────────────────────
interface LeakageWizardProps {
  isZh: boolean;
  canEdit: boolean;
  projectId: number;
  editEntry?: LeakageEntry;
  onSaved: (data: SectionB73Detail) => void;
  onCancel: () => void;
}

const LeakageWizard: React.FC<LeakageWizardProps> = ({
  isZh, canEdit, projectId, editEntry, onSaved, onCancel
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [stage, setStage] = useState<WizardStage>('equations');
  const [entry, setEntry] = useState<LeakageEntry>(editEntry ? { ...editEntry } : EMPTY_ENTRY());
  const [saving, setSaving] = useState(false);
  const [paraTypeCodes, setParaTypeCodes] = useState<SystemCode[]>([]);
  const [formulaConfigs, setFormulaConfigs] = useState<FormulaConfigItem[]>([]);
  const [formulaId, setFormulaId] = useState<number | ''>('');

  useEffect(() => {
    getSystemCodes({ code_etype: 'equation parameters type', is_active: true })
      .then(codes => setParaTypeCodes(codes))
      .catch(() => setParaTypeCodes([]));
  }, []);
  useEffect(() => { formulaConfigService.listActive().then(setFormulaConfigs).catch(() => {}); }, []);

  const stageLabels: Record<WizardStage, string> = {
    equations: t('sectionB73.stageEquations'),
    methodological_choices: t('sectionB73.stageMethodChoice'),
    conservative_estimates: t('sectionB73.stageConservative'),
  };
  const stageOrder: WizardStage[] = ['equations', 'methodological_choices', 'conservative_estimates'];
  const currentIdx = stageOrder.indexOf(stage);

  const addParam = () => {
    setEntry(prev => ({
      ...prev,
      parameters: [...prev.parameters, { symbol: '', para_type: null, scenarios: '', unit: '', annual_value: null }]
    }));
  };

  const updateParam = (idx: number, field: keyof LeakageParameter, value: string | number | null) => {
    setEntry(prev => {
      const params = [...prev.parameters];
      params[idx] = { ...params[idx], [field]: value };
      return { ...prev, parameters: params };
    });
  };

  const removeParam = (idx: number) => {
    setEntry(prev => ({ ...prev, parameters: prev.parameters.filter((_, i) => i !== idx) }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      let result: SectionB73Detail;
      if (editEntry) {
        result = await sectionB73Service.updateLeakageEntry(projectId, entry.id, {
          methodology: entry.methodology,
          equation: entry.equation,
          equation_note: entry.equation_note,
          parameters: entry.parameters,
          calculation_result: entry.calculation_result,
        });
      } else {
        result = await sectionB73Service.addLeakageEntry(projectId, {
          methodology: entry.methodology,
          equation: entry.equation,
          equation_note: entry.equation_note,
          parameters: entry.parameters,
          calculation_result: entry.calculation_result,
        });
      }
      onSaved(result);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || t('sectionB73.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const btnStyle = (primary: boolean, disabled = false) => ({
    padding: '8px 20px', borderRadius: 6, border: 'none',
    background: disabled ? '#ccc' : primary ? '#4A90E2' : '#f0f0f0',
    color: disabled ? '#888' : primary ? '#fff' : '#333',
    cursor: disabled ? 'not-allowed' as const : 'pointer' as const,
    fontWeight: 500, fontSize: 14,
  });

  return (
    <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, padding: 24 }}>
      {/* 步驟列 */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '1px solid #e0e0e0', paddingBottom: 12 }}>
        {stageOrder.map((s, i) => (
          <div key={s} style={{ flex: 1, textAlign: 'center', position: 'relative' }}>
            <div style={{
              width: 28, height: 28, borderRadius: '50%', margin: '0 auto 4px',
              background: i < currentIdx ? '#52c41a' : i === currentIdx ? '#4A90E2' : '#e0e0e0',
              color: i <= currentIdx ? '#fff' : '#999',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 600, fontSize: 13,
            }}>{i < currentIdx ? '✓' : i + 1}</div>
            <div style={{ fontSize: 12, color: i === currentIdx ? '#4A90E2' : '#999', fontWeight: i === currentIdx ? 600 : 400 }}>
              {stageLabels[s]}
            </div>
          </div>
        ))}
      </div>

      {/* Stage 1: 公式 */}
      {stage === 'equations' && (
        <div>
          {/* 適用方法論 */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>
              {t('sectionB73.methodologyLabel')}
            </label>
            <input
              value={entry.methodology}
              onChange={e => setEntry(prev => ({ ...prev, methodology: e.target.value }))}
              disabled={!canEdit}
              placeholder={t('sectionB73.methodologyPlaceholder')}
              style={{
                width: '100%', padding: '8px 12px', border: '1px solid #d0d0d0',
                borderRadius: 6, fontSize: 14, boxSizing: 'border-box',
              }}
            />
          </div>

          {/* 從公式庫選取 */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>{isZh ? '從公式庫選取（選填）' : 'Select from Formula Library (optional)'}</label>
            <select value={formulaId} onChange={e => {
              const id = e.target.value ? Number(e.target.value) : '';
              setFormulaId(id);
              if (!id) return;
              const fc = formulaConfigs.find(f => f.id === id);
              if (fc?.formula_latex) setEntry(prev => ({ ...prev, equation: { latex: fc.formula_latex! } }));
            }} style={{ width: '100%', padding: '8px 12px', border: '1px solid #d0d0d0', borderRadius: 6, fontSize: 14 }}>
              <option value="">-- {isZh ? '手動輸入' : 'Enter manually'} --</option>
              {formulaConfigs.map(f => <option key={f.id} value={f.id}>{f.formula_code}：{isZh ? f.name_zh : f.name_en}</option>)}
            </select>
          </div>

          {/* LaTeX 公式 */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <label style={{ fontWeight: 600 }}>{t('sectionB73.latexEquation')}</label>
            <LatexHelper isZh={isZh} onInsert={snippet =>
              setEntry(prev => ({ ...prev, equation: { latex: prev.equation.latex + snippet } }))
            } />
          </div>
          <textarea
            value={entry.equation.latex}
            onChange={e => setEntry(prev => ({ ...prev, equation: { latex: e.target.value } }))}
            placeholder="e.g. LE_{y} = \sum_{i} (AD_{i,y} \times EF_{i,y})"
            style={{
              width: '100%', minHeight: 80, padding: '10px 12px', fontFamily: 'monospace',
              border: '1px solid #d0d0d0', borderRadius: 6, fontSize: 14, resize: 'vertical', boxSizing: 'border-box',
            }}
            disabled={!canEdit}
          />
          {entry.equation.latex.trim() && (
            <div style={{ marginTop: 12, padding: '12px 16px', background: '#f8f8f8', borderRadius: 6, border: '1px solid #e8e8e8' }}>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>{t('sectionB73.preview')}</div>
              {(() => {
                try { return <BlockMath math={entry.equation.latex} />; }
                catch { return <span style={{ color: '#c33', fontSize: 13 }}>{t('sectionB73.latexError')}</span>; }
              })()}
            </div>
          )}

          {/* 公式差異說明 (equation_note) */}
          <div style={{ marginTop: 16 }}>
            <label style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>
              {t('sectionB73.equationNoteLabel')}
              <span style={{ fontWeight: 400, color: '#999', fontSize: 12, marginLeft: 6 }}>({t('sectionB73.optional')})</span>
            </label>
            <RichTextEditor
              value={entry.equation_note}
              onChange={html => setEntry(prev => ({ ...prev, equation_note: html }))}
              placeholder={t('sectionB73.equationNotePlaceholder')}
              disabled={!canEdit}
              toolbar="inline"
              minHeight="60px"
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
            <button style={btnStyle(false)} onClick={onCancel}>{t('sectionB73.cancel')}</button>
            <button
              style={btnStyle(true, !entry.equation.latex.trim())}
              disabled={!entry.equation.latex.trim()}
              onClick={() => {
                const parsed = parseLatexSymbols(entry.equation.latex);
                const cfgParams = formulaId ? (formulaConfigs.find(f => f.id === formulaId)?.parameters ?? []) : [];
                if (parsed.length > 0) {
                  setEntry(prev => {
                    const existing = new Set(prev.parameters.map(p => p.symbol));
                    const newParams: LeakageParameter[] = parsed
                      .filter(sym => !existing.has(sym))
                      .map(sym => {
                        const cp = cfgParams.find(p => p.symbol === sym);
                        return { symbol: sym, para_type: null, scenarios: cp ? (isZh ? [cp.name_zh, cp.description_zh] : [cp.name_en || cp.name_zh, cp.description_en || cp.description_zh]).filter(Boolean).join(' ') : '', unit: cp?.unit ?? '', annual_value: null };
                      });
                    return { ...prev, parameters: [...prev.parameters, ...newParams] };
                  });
                }
                setStage('methodological_choices');
              }}
            >
              {t('sectionB73.nextMethodChoice')}
            </button>
          </div>
        </div>
      )}

      {/* Stage 2: 方法論說明 */}
      {stage === 'methodological_choices' && (
        <div>
          {/* 公式回顯 */}
          <div style={{ padding: '10px 16px', background: '#f0f7ff', borderRadius: 6, border: '1px solid #bde0ff', marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: '#4A90E2', marginBottom: 4, fontWeight: 600 }}>
              {t('sectionB73.equationPrefix')}
            </div>
            {(() => { try { return <BlockMath math={entry.equation.latex} />; } catch { return null; } })()}
          </div>

          {/* 參數表格 */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <label style={{ fontWeight: 600 }}>{t('sectionB73.parametersLabel')}</label>
              {canEdit && (
                <button style={btnStyle(false)} onClick={addParam}>
                  + {t('sectionB73.addParameter')}
                </button>
              )}
            </div>
            {entry.parameters.length > 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead className="table-header-dark-green">
                  <tr style={{ background: '#f5f5f5' }}>
                    <th style={{ padding: '6px 10px', border: '1px solid #e0e0e0', textAlign: 'left', fontSize: 12 }}>
                      {t('sectionB73.symbol')}
                    </th>
                    <th style={{ padding: '6px 10px', border: '1px solid #e0e0e0', textAlign: 'left', fontSize: 12 }}>
                      {t('sectionB73.paraType')}
                    </th>
                    <th style={{ padding: '6px 10px', border: '1px solid #e0e0e0', textAlign: 'left', fontSize: 12 }}>
                      {t('sectionB73.scenariosLabel')}
                    </th>
                    <th style={{ padding: '6px 10px', border: '1px solid #e0e0e0', textAlign: 'left', fontSize: 12 }}>
                      {t('sectionB73.unitLabel')}
                    </th>
                    <th style={{ padding: '6px 10px', border: '1px solid #e0e0e0', width: 40 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {entry.parameters.map((p, idx) => (
                    <tr key={idx}>
                      <td style={{ padding: '4px 6px', border: '1px solid #e0e0e0' }}>
                        <input value={p.symbol} onChange={e => updateParam(idx, 'symbol', e.target.value)}
                          style={{ width: '100%', border: 'none', outline: 'none', fontFamily: 'monospace', fontSize: 13 }}
                          disabled={!canEdit} placeholder="AD_{i,y}" />
                      </td>
                      <td style={{ padding: '4px 6px', border: '1px solid #e0e0e0', minWidth: 140 }}>
                        <select
                          value={p.para_type ?? ''}
                          onChange={e => updateParam(idx, 'para_type', e.target.value === '' ? null : Number(e.target.value))}
                          disabled={!canEdit}
                          style={{ width: '100%', border: 'none', outline: 'none', fontSize: 13, background: 'transparent' }}
                        >
                          <option value="">{t('sectionB73.allTypes')}</option>
                          {paraTypeCodes.map(c => (
                            <option key={c.id} value={c.id}>{c.code_cname}</option>
                          ))}
                        </select>
                      </td>
                      <td style={{ padding: '4px 6px', border: '1px solid #e0e0e0', minWidth: 200 }}>
                        <RichTextEditor
                          value={p.scenarios}
                          onChange={html => updateParam(idx, 'scenarios', html)}
                          placeholder={t('sectionB73.scenariosLabel')}
                          disabled={!canEdit}
                          minHeight="72px"
                        />
                      </td>
                      <td style={{ padding: '4px 6px', border: '1px solid #e0e0e0' }}>
                        <UnitInput compact value={p.unit} onChange={v => updateParam(idx, 'unit', v)} disabled={!canEdit} />
                      </td>
                      <td style={{ padding: '4px 6px', border: '1px solid #e0e0e0', textAlign: 'center' }}>
                        {canEdit && (
                          <button onClick={() => removeParam(idx)}
                            style={{ background: 'none', border: 'none', color: '#e53935', cursor: 'pointer', fontSize: 16 }}>✕</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ padding: '12px', color: '#aaa', textAlign: 'center', border: '1px dashed #e0e0e0', borderRadius: 6 }}>
                {t('sectionB73.noParams')}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
            <button style={btnStyle(false)} onClick={() => setStage('equations')}>← {t('sectionB73.backToEquations')}</button>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={btnStyle(false)} onClick={onCancel}>{t('sectionB73.cancel')}</button>
              <button style={btnStyle(true)} onClick={() => setStage('conservative_estimates')}>
                {t('sectionB73.nextConservative')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stage 3: 保守估算 */}
      {stage === 'conservative_estimates' && (
        <div>
          {/* 公式回顯 */}
          <div style={{ padding: '10px 16px', background: '#f0f7ff', borderRadius: 6, border: '1px solid #bde0ff', marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: '#4A90E2', marginBottom: 4, fontWeight: 600 }}>
              {t('sectionB73.equationPrefix')}
            </div>
            {(() => { try { return <BlockMath math={entry.equation.latex} />; } catch { return null; } })()}
          </div>

          {/* 參數數值輸入 */}
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontWeight: 600, display: 'block', marginBottom: 8 }}>
              {t('sectionB73.conservativeValue')}
            </label>
            {entry.parameters.length > 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead className="table-header-dark-green">
                  <tr style={{ background: '#f5f5f5' }}>
                    <th style={{ padding: '6px 10px', border: '1px solid #e0e0e0', textAlign: 'left', fontSize: 12 }}>
                      {t('sectionB73.symbol')}
                    </th>
                    <th style={{ padding: '6px 10px', border: '1px solid #e0e0e0', textAlign: 'left', fontSize: 12 }}>
                      {t('sectionB73.conservativeValue')}
                    </th>
                    <th style={{ padding: '6px 10px', border: '1px solid #e0e0e0', textAlign: 'left', fontSize: 12 }}>
                      {t('sectionB73.unitLabel')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {entry.parameters.map((p, idx) => (
                    <tr key={idx}>
                      <td style={{ padding: '4px 10px', border: '1px solid #e0e0e0', fontFamily: 'monospace' }}>
                        {p.symbol || '—'}
                      </td>
                      <td style={{ padding: '4px 6px', border: '1px solid #e0e0e0' }}>
                        <input
                          type="number"
                          step="0.01"
                          value={p.annual_value ?? ''}
                          onChange={e => updateParam(idx, 'annual_value', e.target.value === '' ? null : Number(e.target.value))}
                          style={{ width: '100%', border: 'none', outline: 'none', fontSize: 13 }}
                          disabled={!canEdit}
                        />
                      </td>
                      <td style={{ padding: '4px 10px', border: '1px solid #e0e0e0' }}>
                        {p.unit ? (
                          (() => { try { return <InlineMath math={p.unit} />; } catch { return <span>{p.unit}</span>; } })()
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ padding: '12px', color: '#aaa', textAlign: 'center', border: '1px dashed #e0e0e0', borderRadius: 6 }}>
                {t('sectionB73.noParams')}
              </div>
            )}
          </div>

          {/* 計算區 */}
          <div style={{ marginTop: 16, padding: '14px 16px', background: '#f8faff', borderRadius: 8, border: '1px solid #d0e4ff' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <label style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }}>{t('sectionB73.resultUnit')}</label>
                <div style={{ width: 220 }}>
                  <UnitInput
                    value={entry.calculation_result.unit}
                    onChange={v => setEntry(prev => ({ ...prev, calculation_result: { ...prev.calculation_result, unit: v } }))}
                    disabled={!canEdit}
                    compact
                  />
                </div>
              </div>
              {canEdit && (
                <button
                  onClick={() => {
                    const val = evaluateLatex(entry.equation.latex, entry.parameters);
                    if (val !== null) {
                      setEntry(prev => ({ ...prev, calculation_result: { ...prev.calculation_result, value: val } }));
                    } else {
                      toast.warning(t('sectionB73.calcAutoError'));
                    }
                  }}
                  style={{
                    padding: '7px 20px', background: '#52c41a', color: '#fff',
                    border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13
                  }}
                >
                  ▶ {t('sectionB73.calculating')}
                </button>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
                <span style={{ fontSize: 13, color: '#888' }}>{t('sectionB73.calcResult')}</span>
                <span style={{
                  fontSize: 20, fontWeight: 700,
                  color: entry.calculation_result.value !== null ? '#1a1a1a' : '#ccc',
                  minWidth: 80, textAlign: 'right',
                }}>
                  {fmtNum(entry.calculation_result.value)}
                </span>
                {entry.calculation_result.unit && (
                  <span style={{ fontSize: 13, color: '#555' }}>
                    {(() => { try { return <InlineMath math={entry.calculation_result.unit} />; } catch { return entry.calculation_result.unit; } })()}
                  </span>
                )}
              </div>
            </div>
            {entry.calculation_result.value !== null && (
              <div style={{ marginTop: 8, fontSize: 12, color: '#52c41a' }}>
                ✓ {t('sectionB73.calcConfirm')}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
            <button style={btnStyle(false)} onClick={() => setStage('methodological_choices')}>← {t('sectionB73.backToMethodChoice')}</button>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={btnStyle(false)} onClick={onCancel}>{t('sectionB73.cancel')}</button>
              {canEdit && (
                <button style={btnStyle(true, saving)} disabled={saving} onClick={handleSave}>
                  {saving ? t('sectionB73.saving') : t('sectionB73.save')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── 公式 DataTable ────────────────────────────────────────────────
interface LeakageTableProps {
  canEdit: boolean;
  entries: LeakageEntry[];
  onEdit: (entry: LeakageEntry) => void;
  onDelete: (entryId: string) => void;
  onAdd: () => void;
}

const LeakageTable: React.FC<LeakageTableProps> = ({ canEdit, entries, onEdit, onDelete, onAdd }) => {
  const { t } = useTranslation();
  return (
    <div>
      {canEdit && (
        <div style={{ marginBottom: 12 }}>
          <button onClick={onAdd} style={{
            padding: '8px 18px', background: '#4A90E2', color: '#fff', border: 'none',
            borderRadius: 6, cursor: 'pointer', fontWeight: 500
          }}>
            + {t('sectionB73.addLeakage')}
          </button>
        </div>
      )}
      {entries.length === 0 ? (
        <div style={{ padding: '32px', textAlign: 'center', color: '#aaa', border: '1px dashed #e0e0e0', borderRadius: 8 }}>
          {t('sectionB73.noData')}
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table" style={{ fontSize: 13 }}>
            <thead className="table-header-dark-green">
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th>{t('sectionB73.colMethodology')}</th>
                <th>{t('sectionB73.colEquation')}</th>
                <th>{t('sectionB73.colParameters')}</th>
                <th>{t('sectionB73.colResult')}</th>
                {canEdit && <th style={{ width: 160 }}>{t('sectionB73.colActions')}</th>}
              </tr>
            </thead>
            <tbody>
              {entries.map((e, idx) => (
                <tr key={e.id}>
                  <td style={{ textAlign: 'center' }}>{idx + 1}</td>
                  <td>
                    <div style={{ fontWeight: 500 }}>{e.methodology || '-'}</div>
                  </td>
                  <td>
                    {e.equation.latex ? (
                      <div style={{ maxWidth: 280, overflow: 'auto' }}>
                        {(() => { try { return <BlockMath math={e.equation.latex} />; } catch { return <code>{e.equation.latex}</code>; } })()}
                      </div>
                    ) : '-'}
                  </td>
                  <td>
                    {e.parameters.length > 0 ? (
                      <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12 }}>
                        {e.parameters.map((p, pi) => (
                          <li key={pi}>
                            <code>{p.symbol}</code>
                            {p.annual_value != null && (
                              <span> = {fmtNum(p.annual_value)}{' '}
                                {p.unit && (
                                  (() => { try { return <InlineMath math={p.unit} />; } catch { return <span>{p.unit}</span>; } })()
                                )}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : '-'}
                  </td>
                  <td>
                    {e.calculation_result.value != null ? (
                      <span style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                        <span>{fmtNum(e.calculation_result.value)}</span>
                        {e.calculation_result.unit && (
                          <span style={{ fontSize: 12 }}>
                            {(() => { try { return <InlineMath math={e.calculation_result.unit} />; } catch { return e.calculation_result.unit; } })()}
                          </span>
                        )}
                      </span>
                    ) : '-'}
                  </td>
                  {canEdit && (
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => onEdit(e)}
                          style={{ padding: '6px 16px', background: '#fff', border: '1px solid #4A90E2', color: '#4A90E2', borderRadius: 5, cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
                          {t('sectionB73.edit')}
                        </button>
                        <button onClick={() => onDelete(e.id)}
                          style={{ padding: '6px 16px', background: '#fff', border: '1px solid #e53935', color: '#e53935', borderRadius: 5, cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
                          {t('sectionB73.delete')}
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ── 主頁面 ────────────────────────────────────────────────────────
const SectionB73Page: React.FC = () => {
  const { i18n, t } = useTranslation();
  const { hasPermission, loading: permissionLoading } = usePermission();
  const { selectedProjectId } = useProject();
  const toast = useToast();
  const hasInitialized = useRef(false);
  const isZh = i18n.language === 'zh-TW';

  const [activeTab, setActiveTab] = useState(0);
  const [data, setData] = useState<SectionB73Detail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 公式精靈狀態
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editEntry, setEditEntry] = useState<LeakageEntry | undefined>(undefined);

  // Tab 1 / Tab 2 表單狀態
  const [leakageSources, setLeakageSources] = useState('');
  const [leakageHandling, setLeakageHandling] = useState('');
  const [savingTab1, setSavingTab1] = useState(false);
  const [savingTab2, setSavingTab2] = useState(false);

  // Session
  useEffect(() => {
    if (!hasInitialized.current && !permissionLoading) {
      validateSession('SectionB73Page');
      hasInitialized.current = true;
    }
  }, [permissionLoading]);

  // 載入資料
  const loadData = useCallback(async (projectId: number) => {
    try {
      setLoading(true);
      setError(null);
      const res = await sectionB73Service.getSectionB73ByProject(projectId);
      setData(res);
      if (res) {
        setLeakageSources(res.leakage_sources || '');
        setLeakageHandling(res.leakage_handling || '');
      }
    } catch (err: any) {
      if (err.response?.status === 404 || !err.response) {
        setData(null);
      } else {
        setError(err.response?.data?.detail || '載入資料失敗');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedProjectId) { setData(null); setLeakageSources(''); setLeakageHandling(''); return; }
    loadData(selectedProjectId);
  }, [selectedProjectId, loadData]);

  const canEdit = hasPermission('section_b7_3', 'update');

  // 儲存 Tab 1
  const handleSaveTab1 = async () => {
    if (!selectedProjectId) return;
    try {
      setSavingTab1(true);
      const res = await sectionB73Service.updateSectionB73(selectedProjectId, { leakage_sources: leakageSources });
      setData(res);
      toast.success(t('sectionB73.saveSuccess'));
    } catch (err: any) {
      toast.error(err.response?.data?.detail || t('sectionB73.saveFailed'));
    } finally {
      setSavingTab1(false);
    }
  };

  // 儲存 Tab 2
  const handleSaveTab2 = async () => {
    if (!selectedProjectId) return;
    try {
      setSavingTab2(true);
      const res = await sectionB73Service.updateSectionB73(selectedProjectId, { leakage_handling: leakageHandling });
      setData(res);
      toast.success(t('sectionB73.saveSuccess'));
    } catch (err: any) {
      toast.error(err.response?.data?.detail || t('sectionB73.saveFailed'));
    } finally {
      setSavingTab2(false);
    }
  };

  // 重置
  const handleReset = async () => {
    if (!selectedProjectId) return;
    const ok = await toast.confirm({
      title: isZh ? '操作確認' : 'Confirmation',
      message: t('sectionB73.resetConfirm'),
      confirmText: isZh ? '重設' : 'Reset',
    });
    if (!ok) return;
    try {
      setLoading(true);
      await sectionB73Service.resetSectionB73(selectedProjectId);
      setData(null);
      setLeakageSources('');
      setLeakageHandling('');
      setWizardOpen(false);
      setEditEntry(undefined);
      setActiveTab(0);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || t('sectionB73.saveFailed'));
    } finally {
      setLoading(false);
    }
  };

  // 刪除公式條目
  const handleDelete = async (entryId: string) => {
    if (!selectedProjectId) return;
    const ok = await toast.confirm({
      title: isZh ? '刪除確認' : 'Delete Confirmation',
      message: t('sectionB73.deleteConfirm'),
      confirmText: isZh ? '刪除' : 'Delete',
      severity: 'error',
    });
    if (!ok) return;
    try {
      await sectionB73Service.deleteLeakageEntry(selectedProjectId, entryId);
      await loadData(selectedProjectId);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || t('sectionB73.deleteFailed'));
    }
  };

  // 精靈回調
  const handleWizardSaved = (res: SectionB73Detail) => {
    setData(res);
    setWizardOpen(false);
    setEditEntry(undefined);
  };

  const tabs = [
    { label: t('sectionB73.tab1') },
    { label: t('sectionB73.tab2') },
    { label: t('sectionB73.tab3') },
  ];

  if (permissionLoading) return <div className="page-container"><div className="loading">Loading...</div></div>;
  if (!hasPermission('section_b7_3', 'read')) return <div className="page-container"><div className="error-message">{t('sectionB73.noPermission')}</div></div>;

  return (
    <div className="page-container">
      <div className="page-header">
        <FunctionPageHeader funcCode="section_b7_3" />
        {canEdit && selectedProjectId && data && (
          <div className="page-header-right">
            <button
              onClick={handleReset}
              style={{
                padding: '8px 16px', borderRadius: 6, border: '1px solid #e53935',
                background: '#fff', color: '#e53935', cursor: 'pointer',
                fontWeight: 500, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6,
              }}
              title={t('sectionB73.resetTitle')}
            >
              🗑 {t('sectionB73.resetData')}
            </button>
          </div>
        )}
      </div>

      {error && <div className="error-message" style={{ marginBottom: 16 }}>{error}</div>}

      {!selectedProjectId ? (
        <div style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: 16 }}>
          {t('sectionB73.selectProject')}
        </div>
      ) : loading ? (
        <div className="loading">{t('sectionB73.loading')}</div>
      ) : (
        <div>
          {/* Tab Bar */}
          <div style={{ display: 'flex', borderBottom: '2px solid #e0e0e0', marginBottom: 20, flexWrap: 'wrap' }}>
            {tabs.map((tab, i) => (
              <button key={i} onClick={() => { setActiveTab(i); setWizardOpen(false); setEditEntry(undefined); }}
                style={{
                  padding: '10px 18px', border: 'none', cursor: 'pointer', fontSize: 13,
                  borderBottom: activeTab === i ? '3px solid #4A90E2' : '3px solid transparent',
                  background: activeTab === i ? '#f0f7ff' : 'transparent',
                  color: activeTab === i ? '#4A90E2' : '#666',
                  fontWeight: activeTab === i ? 600 : 400,
                  marginBottom: -2,
                }}>
                {tab.label}
              </button>
            ))}
          </div>

          {/* B.7.3.1 洩漏來源說明 */}
          {activeTab === 0 && (
            <div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontWeight: 600, display: 'block', marginBottom: 8 }}>
                  {t('sectionB73.leakageSourcesLabel')}
                </label>
                <RichTextEditor
                  value={leakageSources}
                  onChange={html => setLeakageSources(html)}
                  placeholder={t('sectionB73.leakageSourcesPlaceholder')}
                  disabled={!canEdit}
                />
              </div>
              {canEdit && (
                <div style={{ textAlign: 'right' }}>
                  <button
                    onClick={handleSaveTab1}
                    disabled={savingTab1}
                    style={{
                      padding: '8px 24px', background: savingTab1 ? '#ccc' : '#4A90E2',
                      color: '#fff', border: 'none', borderRadius: 6,
                      cursor: savingTab1 ? 'not-allowed' : 'pointer',
                      fontWeight: 500, fontSize: 14,
                    }}
                  >
                    {savingTab1 ? t('sectionB73.saving') : t('sectionB73.save')}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* B.7.3.2 洩漏排放處理方法 */}
          {activeTab === 1 && (
            <div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontWeight: 600, display: 'block', marginBottom: 8 }}>
                  {t('sectionB73.leakageHandlingLabel')}
                </label>
                <RichTextEditor
                  value={leakageHandling}
                  onChange={html => setLeakageHandling(html)}
                  placeholder={t('sectionB73.leakageHandlingPlaceholder')}
                  disabled={!canEdit}
                />
              </div>
              {canEdit && (
                <div style={{ textAlign: 'right' }}>
                  <button
                    onClick={handleSaveTab2}
                    disabled={savingTab2}
                    style={{
                      padding: '8px 24px', background: savingTab2 ? '#ccc' : '#4A90E2',
                      color: '#fff', border: 'none', borderRadius: 6,
                      cursor: savingTab2 ? 'not-allowed' : 'pointer',
                      fontWeight: 500, fontSize: 14,
                    }}
                  >
                    {savingTab2 ? t('sectionB73.saving') : t('sectionB73.save')}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* B.7.3.3 洩漏排放計算 */}
          {activeTab === 2 && (
            <div>
              {wizardOpen ? (
                <LeakageWizard
                  isZh={isZh}
                  canEdit={canEdit}
                  projectId={selectedProjectId!}
                  editEntry={editEntry}
                  onSaved={handleWizardSaved}
                  onCancel={() => { setWizardOpen(false); setEditEntry(undefined); }}
                />
              ) : (
                <LeakageTable
                  canEdit={canEdit}
                  entries={data?.leakage_calculate ?? []}
                  onAdd={() => { setEditEntry(undefined); setWizardOpen(true); }}
                  onEdit={e => { setEditEntry(e); setWizardOpen(true); }}
                  onDelete={handleDelete}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SectionB73Page;
